import FreeTrialRestriction from '../interfaces/free-trial-restriction.interface';
import store from '../../../store/store';
import { Dispatch } from 'redux';
import { FreeTrialConversionProduct } from '../interfaces/free-trial-conversion.interface';
import { Product } from '../../cart/shop-cart/interfaces/shop-cart.interface';
import { StoreState } from '../../../store/store.interface';
import { addToCart } from '../../cart/shop-cart/thunks/add-to-cart.thunk';
import { addToCartUrlContext } from '../../../common/utils/cart-util';
import { clearFreeTrialConvertedIsbns, updateFreeTrialConvertedIsbns } from '../store/free-trial.store';
import { getCartContext } from '../../cart/shop-cart/store/shop-cart.selectors';
import { getFreeTrialConvertedIsbns } from '../store/free-trial.selector';
import { getQueryParam } from '../../../common/utils/util';

/**
 * Indicates that free trial to purchase product conversion must be done for international checkouts.
 */
export const FT_CONVERT_INTL = 'ft_convert_intl';

export class FreeTrialPurchaseConverter {
  /**
   * Clears the free trial conversion messages.
   */
  cleanMessages(): void {
    clearFreeTrialConvertedIsbns();
  }

  /**
   * Converts free trial products having a specific error to purchasable products.
   *
   * It can also force the conversion of all free trial products if required.
   *
   * @param products
   * @param forceConvert
   */
  convertProducts(products: Product[], forceConvert = false): FreeTrialConversionProduct[] {
    const result: FreeTrialConversionProduct[] = [];
    const errors: string[] = [
      FreeTrialRestriction.FREE_TRIAL_ALREADY_SUBSCRIBED,
      FreeTrialRestriction.PRODUCT_ALREADY_PURCHASED,
    ];

    for (const product of products) {
      if (!product.freeTrialInfo?.isFreeTrial) {
        continue;
      }

      if (errors.includes(product.availabilityInfo?.errorCode!) || forceConvert) {
        result.push(this.convertProduct(product));
      }
    }

    return result;
  }

  /**
   * Adds the free trial products to the cart as a regular purchase.
   *
   * @param products
   */
  async purchaseProducts(products: FreeTrialConversionProduct[]): Promise<Awaited<Dispatch | void>[]> {
    const cartContext = getCartContext(store.getState() as StoreState);
    const promises: Promise<Dispatch | void>[] = [];

    for (const product of products) {
      const urlContext = { ...addToCartUrlContext(cartContext, product.requestProduct) };
      urlContext.forceNewProduct = true;

      promises.push(
        store.dispatch(addToCart(urlContext)).then((): void => {
          store.dispatch(updateFreeTrialConvertedIsbns(product.isbn));
        })
      );
    }

    return Promise.all(promises);
  }

  /**
   * Converts a product a request object.
   *
   * @param product
   * @private
   */
  private convertProduct(product: Product): FreeTrialConversionProduct {
    return {
      isbn: product.productIdentifier.isbn10 || '',
      requestProduct: {
        businessUnit: product.productIdentifier.businessUnit,
        isbn: product.productIdentifier.isbn10 || '',
        quantity: product.quantity,
        isbn13: product.productIdentifier.isbn13,
      },
    };
  }
}

/**
 * Verifies if the free trial conversion should be done for international checkouts.
 */
export function shouldConvertFreeTrialForInternational(): boolean {
  return getQueryParam(FT_CONVERT_INTL) === 'true';
}

/**
 * Verifies if the product has been converted from free trial to purchasable.
 *
 * @param isbn
 */
export function wasFreeTrialConverted(isbn?: string): boolean {
  return !!isbn && getFreeTrialConvertedIsbns(store.getState() as StoreState).includes(isbn);
}
